/**
 * Affiliate referral-code and "how did you hear" rules.
 *
 * Business rules rather than UI, so both platforms and the control panel read
 * the same format. `now` is injectable because the code embeds the current
 * month and year and would otherwise be untestable.
 */
import {
  AFFILIATE_HOW_HEARD_OPTIONS,
  AFFILIATE_HOW_HEARD_OTHER,
} from '../constants/affiliate';

const CODE_PREFIX = 'WLB';
const INITIALS_LENGTH = 3;
/** Stands in for a name with too few letters to fill the suffix. */
const INITIALS_FALLBACK = 'USR';

/**
 * `WLB` + 2-digit month + 2-digit year + 4 random digits + 3 letters from the
 * affiliate's name, matching the codes already in circulation so support can
 * read a code back to its owner.
 */
export const generateAffiliateReferralCode = (
  affiliateName: string,
  now: Date = new Date(),
): string => {
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const year = String(now.getFullYear()).slice(-2);
  const random = Math.floor(1000 + Math.random() * 9000);
  const letters = affiliateName.replace(/[^a-zA-Z]/g, '').toUpperCase();
  const initials = (letters || INITIALS_FALLBACK)
    .slice(0, INITIALS_LENGTH)
    .padEnd(INITIALS_LENGTH, INITIALS_FALLBACK[INITIALS_LENGTH - 1]);
  return `${CODE_PREFIX}${month}${year}${random}${initials}`;
};

export interface AffiliateHowHeardSelection {
  /** One of `AFFILIATE_HOW_HEARD_OPTIONS`, or `Other` for a free-text answer. */
  option: string;
  /** The free-text answer; empty unless `option` is `Other`. */
  specify: string;
}

/**
 * The API stores a single string, so an answer that is not one of the offered
 * options is by definition the free-text "Other" — including a campaign slug
 * captured from `/ace` or `/cee`.
 */
export const splitAffiliateHowHeard = (
  stored: string | null | undefined,
): AffiliateHowHeardSelection => {
  const value = (stored ?? '').trim();
  if (!value) {
    return { option: '', specify: '' };
  }
  const known = AFFILIATE_HOW_HEARD_OPTIONS.some(
    option => option.value !== AFFILIATE_HOW_HEARD_OTHER && option.value === value,
  );
  return known
    ? { option: value, specify: '' }
    : { option: AFFILIATE_HOW_HEARD_OTHER, specify: value };
};

/** The value to send for a selection, collapsing `Other` to its free text. */
export const affiliateHowHeardValue = (
  selection: AffiliateHowHeardSelection,
): string =>
  selection.option === AFFILIATE_HOW_HEARD_OTHER
    ? selection.specify.trim()
    : selection.option;
